import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Eye, Copy, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import type { GeneralRequestForm } from '@/types';

export default function FormsList() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: forms, isLoading } = useQuery<GeneralRequestForm[]>({
    queryKey: ['venue-forms', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (formId: string) => {
      await api.delete(`/admin/venues/${venueId}/forms/${formId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-forms', venueId] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (formId: string) => {
      const response = await api.post(`/admin/venues/${venueId}/forms/${formId}/duplicate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-forms', venueId] });
    },
  });

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">General Request Forms</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - Custom Form Builder
          </p>
        </div>
        <Link
          to={`/venues/${venueId}/forms/new`}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Form
        </Link>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fields
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Settings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  </tr>
                ))
              ) : forms?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No forms created yet. Click "Create Form" to build your first form.
                  </td>
                </tr>
              ) : (
                forms?.map((form) => (
                  <tr key={form.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{form.name}</p>
                        {form.description && (
                          <p className="text-sm text-gray-500 truncate max-w-xs">{form.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {form.field_count || 0} fields
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {form.submission_count || 0} submissions
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1 text-xs text-gray-500">
                        {form.requires_approval && (
                          <span className="text-amber-600">Requires approval</span>
                        )}
                        {form.send_confirmation_email && (
                          <span className="text-green-600">Sends confirmation</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        form.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/venues/${venueId}/forms/${form.id}`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit Form"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/venues/${venueId}/forms/${form.id}/submissions`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="View Submissions"
                        >
                          <FileText className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/venues/${venueId}/forms/${form.id}/preview`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Preview Form"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => duplicateMutation.mutate(form.id)}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Duplicate Form"
                          disabled={duplicateMutation.isPending}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this form?')) {
                              deleteMutation.mutate(form.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete Form"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
